import Vue from 'vue'
import h from './filter-store-helpers'

export default {
  // **************   FILTERING   ******************* //

  filterAllItems(
    { state, commit, dispatch },
    payload = {
      pushState: true,
      replaceState: false,
    }
  ) {
    commit('setFilteredCards', state.cards)
    commit('setCardsFilteredWithPromos', state.cards)
    dispatch('resetHiddenSecondaries')
    h.runAllSingleItemFilters(state, commit)
    h.buildResults(state, commit)

    Vue.nextTick(function() {
      dispatch('filterAllVariants', payload)
    })
  },

  filterAllVariants(
    { state, dispatch, commit },
    payload = {
      pushState: true,
      replaceState: false,
    }
  ) {
    // Reset (deep copy) all available variants before filtering
    h.deepCopyVariants(state)

    // Run all variant filters
    Object.keys(state.variantFilters.selected).forEach(filterId => {
      h.runSingleVariantFilter(state, commit, filterId)
    })
    const cardsSorted = h.getSortedCards(state.cardsFiltered, state.currentSorting)
    const cardsWithoutEmptyVariants = h.getCardsWithoutEmptyVariants(cardsSorted)
    commit('resetDisplayedFilteredCards')
    commit('setFilteredCards', cardsWithoutEmptyVariants)
    dispatch('setFilteredCardsWithPromos', cardsWithoutEmptyVariants)

    // Initial switch is not triggered by leave event in products
    if (!state.appIsInitialized) {
      Vue.nextTick(() => commit('switchDisplayedCards'))
      state.appIsInitialized = true
    }

    dispatch('updateTitleBox')
    dispatch('collectAllActiveFilters')
    if (state.cardsFiltered[0])
      commit('nav/setActiveCardVariant', state.cardsFiltered[0].variants[0], { root: true })
    if (payload.pushState) dispatch('updateUrl', payload)

    // Don't track pageviews on initial load and on popstate navigating back
    // => these views are already tracked
    state.pageviewsAreTracked ? window.gaSendPageview() : commit('trackPageViews', true)
    h.reportLoadingOfProducts(state)
  },

  // **************   RESETTING   ******************* //
  resetAndReinitAllFilters(
    { dispatch },
    payload = {
      pushState: false,
    }
  ) {
    dispatch('resetAllFilters')
    dispatch('reinitAllFilters', payload)
  },

  resetAllFilters({ state, commit }, payload = { forcePrimaryReset: false }) {
    Object.keys(state.itemFilters.selected).forEach(key => {
      const isPrimaryFilter = h.isPrimaryFilter(state, key)
      // Primary filters are not reset by definition
      if (!isPrimaryFilter || payload.forcePrimaryReset) {
        const value = isPrimaryFilter ? [''] : []
        commit('setItemFilterOption', {
          id: key,
          value,
        })
      }
    })

    Object.keys(state.variantFilters.selected).forEach(key => {
      const isPrimaryFilter = h.isPrimaryFilter(state, key)
      if (!isPrimaryFilter || payload.forcePrimaryReset) {
        const value = isPrimaryFilter ? [''] : []
        commit('setVariantFilterOption', {
          id: key,
          value,
        })
      }
    })
  },

  reinitAllFilters(
    { commit, dispatch },
    payload = {
      reset: true,
      pushState: false,
    }
  ) {
    commit('broadcastFilterReset')
    commit('broadcastFilterStateUpdate')
    dispatch('filterAllItems', payload)
  },

  resetHiddenSecondaries({ state, commit }) {
    h.determineHiddenSecondaries(state, commit)
    state.hiddenSecondaries.forEach(filterName => {
      if (state.itemFilters.selected.hasOwnProperty(filterName)) {
        commit('setItemFilterOption', {
          id: filterName,
          value: [],
        })
      } else {
        commit('setVariantFilterOption', {
          id: filterName,
          value: [],
        })
      }
    })
  },

  determineHiddenSecondaries({ state, commit }) {
    h.determineHiddenSecondaries(state, commit)
  },

  setFilteredCardsWithPromos({ state, getters, commit }, cardsWithoutEmptyVariants) {
    const cardsFilteredWithPromos = getters.isPromoCardBeforeProductGroup
      ? h.getCardsWithProductGroupPromoCards({
        cards: cardsWithoutEmptyVariants,
        promoCards: state.promoCards,
        currentSorting: state.currentSorting,
        tags: state.tags,
        activePrimaryFilters: state.activePrimaryFilters,
      })
      : h.getCardsWithPromoCards({
        cards: cardsWithoutEmptyVariants,
        currentBreakpoint: state.currentBreakpoint,
        promoCards: state.promoCards,
      })
    commit('setCardsFilteredWithPromos', cardsFilteredWithPromos)
  },

  // NAVIGATION
  updateUrl({ state, commit, dispatch }, payload) {
    const url = h.composeUrl(state)
    const stateAction = payload.replaceState ? 'replaceState' : 'pushState'
    commit('setCurrentUrl', url)
    window.history[stateAction](
      {
        ...window.history.state,
        url,
      },
      '',
      url
    )

    dispatch('updateDYPageContext')
  },

  updateDYPageContext({ state }) {
    const filters = h.getPathAndQueryFilters(state)
    const categories = filters.pathFilters.filter(f => On.DynamicYield.categoryValues.includes(f))

    On.DynamicYield.pageContext = {
      lng: On.Store.state.page.country,
      type: 'CATEGORY',
      data: categories,
    }
    On.DynamicYield.updatePageContext()
  },

  collectAllActiveFilters({ state, commit }) {
    const filters = h.getPathAndQueryFilters(state)
    commit('addToPathFilters', filters.pathFilters)
    commit('addToQueryFilters', filters.queryFilters)
  },

  updateTitleBox({ state, commit }) {
    let activeFilters = []
    const subPageRelevantVariantFilters = ['gender', 'release_date']

    // All item filters are relevant for determining a subpage
    Object.values(state.itemFilters.selected).forEach(filterOptions => {
      // But only if not combined (e.g. 'race' and 'training' together would never match a subpage)
      if (filterOptions.length === 1 && filterOptions[0] !== '')
        activeFilters.push(filterOptions[0])
    })

    // Only gender and release_date are relevant variant filters
    subPageRelevantVariantFilters.forEach(key => {
      if (state.variantFilters.selected[key] === undefined) return
      Object.values(state.variantFilters.selected[key]).forEach(option => {
        if (option != '') activeFilters.push(option)
      })
    })

    let currentSubPage = h.getCurrentSubPage(state.subPages, activeFilters)
    commit('setActiveSubPage', currentSubPage[0])
  },

  setVpWidth({ state, commit }, payload) {
    commit('setIsMobile', payload)

    // Show only relevant filters after resizing
    Vue.nextTick(function() {
      h.determineHiddenSecondaries(state, commit)
    })
  },

  // **************   TAGGING   ******************* //
  addTag({ state, commit }, payload) {
    let updatedTags = state.tags
    payload.add
      ? updatedTags.push(payload)
      : (updatedTags = updatedTags.filter(tag => {
        return tag.value !== payload.value
      }))
    commit('setTags', updatedTags)
  },

  setInitialTags({ state, commit }) {
    const tagsForItemFilters = h.createTagsFromFilters({
      state,
      filters: state.itemFilters.selected,
      tags: [],
      level: 'item',
    })
    const tagsForAllFilters = h.createTagsFromFilters({
      state,
      filters: state.variantFilters.selected,
      tags: tagsForItemFilters,
      level: 'variant',
    })
    const tagsWithValues = h.setTagValues(state, tagsForAllFilters)
    commit('setTags', tagsWithValues)
  },
}
